import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Card,
  Table,
  Input,
  Select,
  Button,
  Space,
  Typography,
  Dropdown,
  Menu,
  message,
  App,
  Empty,
} from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
import Sidebar from "../../components/Sidebar";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import theme from "../../themes";
import { ReactComponent as SearchIcon } from "../../assets/icons/search.svg";
import { ReactComponent as AddIcon } from "../../assets/icons/add.svg";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  createAgentThunk,
  deleteAgentThunk,
  getAgentsThunk,
} from "../../store/actions/agent.action";
import CreateAgentModal from "./CreateAgentModal";
import { ICreateAgent } from "../../models/agent.model";
import { ROLES } from "../../models/user.model";
import dayjs from "dayjs";
import ToastMessage from "../../utils/toastMessage";
import type { SortOrder } from "antd/es/table/interface";
import { ReactComponent as ArrowUpIcon } from "../../assets/icons/arrowUp.svg";
import { ReactComponent as ArrowDownIcon } from "../../assets/icons/arrowDown.svg";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/checkCircle.svg";
import {
  formatNumber,
  formatSecondsToTime,
} from "../../utils/formatDecimalNumber";
import { useTranslation } from "react-i18next";

const { Option } = Select;

const AgentsPage: React.FC = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { agents, loading } = useAppSelector((state) => state.agent);
  const { user } = useAppSelector((state) => state.auth);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sorter, setSorter] = useState<{
    field: "createdAt" | "agent_name";
    order: SortOrder | undefined;
  }>({ field: "createdAt", order: "descend" });
  const { message } = App.useApp();
  const toast = ToastMessage(message);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [search]);

  // Fetch agents with pagination, search, and sorting
  const fetchAgents = async (
    page = currentPage,
    keyword = debouncedSearch,
    sortField: "createdAt" | "agent_name" = sorter.field,
    sortOrder?: SortOrder
  ) => {
    try {
      const sortBy: Record<string, 1 | -1> = {};
      if (sortField === "createdAt" || sortField === "agent_name") {
        if (sortOrder) {
          sortBy[sortField] = sortOrder === "ascend" ? 1 : -1;
        }
      }
      await dispatch(
        getAgentsThunk({
          limit: pageSize,
          page,
          keyword,
          sort: Object.keys(sortBy).length ? sortBy : undefined,
        })
      ).unwrap();
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleCreateAgentSuccess = () => {
    try {
      fetchAgents(1, debouncedSearch); // Refresh to first page after create
    } catch (error) {
      toast.error(t("agents.failedToCreateAgent"), 5);
    } finally {
      setIsModalOpen(false);
    }
  };

  const handleCustomSort = (field: "createdAt" | "agent_name") => {
    let nextOrder: SortOrder = "descend";
    if (sorter.field === field) {
      nextOrder = sorter.order === "ascend" ? "descend" : "ascend";
    }
    setSorter({ field, order: nextOrder });
    fetchAgents(currentPage, debouncedSearch, field, nextOrder);
  };

  const columns = [
    {
      title: (
        <span
          style={{
            cursor: "pointer",
            color: sorter.field === "agent_name" ? "#000" : "inherit",
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleCustomSort("agent_name");
          }}
        >
          {t("agents.agentName")}{" "}
          {sorter.field === "agent_name" &&
            (sorter.order === "ascend" ? (
              <ArrowUpIcon style={{ marginLeft: 4, verticalAlign: "middle" }} />
            ) : (
              <ArrowDownIcon
                style={{ marginLeft: 4, verticalAlign: "middle" }}
              />
            ))}
        </span>
      ),
      dataIndex: "agent_name",
      key: "agent_name",
      sorter: false, // disable AntD's built-in sorter
      sortOrder: undefined,
      render: (text: string, record: any) => (
        <a
          style={{ color: "#1890ff", fontWeight: 500, cursor: "pointer" }}
          onClick={() => navigate(`/agents/${record.key}`)}
        >
          {text}
        </a>
      ),
      onHeaderCell: () => ({ style: { cursor: "pointer" } }),
    },
    {
      title: t("agents.numberOfCalls"),
      dataIndex: "calls",
      key: "calls",
    },
    {
      title: t("agents.averageDuration"),
      dataIndex: "duration",
      key: "duration",
      render: (text: string) => {
        return <span>{formatSecondsToTime(Number(text))}</span>;
      },
    },
    {
      title: t("agents.completionRate"),
      dataIndex: "rate",
      key: "rate",
    },
    {
      title: (
        <span
          style={{
            cursor: "pointer",
            color: sorter.field === "createdAt" ? "#000" : "inherit",
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleCustomSort("createdAt");
          }}
        >
          {t("agents.createdTime")}{" "}
          {sorter.field === "createdAt" &&
            (sorter.order === "ascend" ? (
              <ArrowUpIcon style={{ marginLeft: 4, verticalAlign: "middle" }} />
            ) : (
              <ArrowDownIcon
                style={{ marginLeft: 4, verticalAlign: "middle" }}
              />
            ))}
        </span>
      ),
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: false,
      sortOrder: undefined,
      onHeaderCell: () => ({ style: { cursor: "pointer" } }),
    },
    {
      title: "",
      key: "action",
      render: (_: any, record: any) => {
        const menuItems = [
          {
            key: "edit",
            label: t("common.edit"),
            onClick: () => navigate(`/agents/${record.key}`),
          },
        ];

        return (
          <Dropdown
            menu={{
              items: menuItems,
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button type="text" icon={<EllipsisOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  // Reset to page 1 when debounced search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  // Fetch agents when currentPage, debounced search, or selectedTimeRange changes
  useEffect(() => {
    fetchAgents(currentPage, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch]);

  return (
    <FlexRow>
      <MainContent>
        <HeaderRow>
          <Title>{t("agents.title")}</Title>
          {user?.role === ROLES.ADMIN && (
            <CreateButton onClick={() => setIsModalOpen(true)}>
              <AddIcon /> {t("agents.createAgent")}
            </CreateButton>
          )}
        </HeaderRow>
        <CardWrapper>
          <Space
            style={{
              marginBottom: 16,
              width: "100%",
              justifyContent: "flex-end",
              display: "flex",
            }}
          >
            <SearchWrapper>
              <SearchIcon />
              <CustomSearch
                placeholder={t("agents.searchAgent")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </SearchWrapper>
          </Space>
          <StyledTable
            dataSource={agents.agents.map((agent) => ({
              key: agent._id,
              agent_name: agent.agent_name,
              calls: agent.stats.number_of_calls,
              duration: agent.stats.total_second / agent.stats.number_of_calls || 0,
              rate: `${formatNumber(
                Number(agent.stats.completion_rate) * 100
              )}%`,
              createdAt: dayjs(agent.createdAt).format("h:mm A, MMM D, YYYY"),
            }))}
            loading={loading}
            columns={columns}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: agents.pagination.totalItems,
              position: ["bottomCenter"],
              onChange: (page) => setCurrentPage(page),
              showSizeChanger: false,
            }}
            rowKey="key"
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <EmptyStateTitle>{t("common.noData")}</EmptyStateTitle>
                  }
                />
              ),
            }}
          />
        </CardWrapper>
        <CreateAgentModal
          visible={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          onCreateSuccess={handleCreateAgentSuccess}
        />
      </MainContent>
    </FlexRow>
  );
};

export default AgentsPage;

// Styled Components
const FlexRow = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${theme.colors.grey3};
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
`;

const CreateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${theme.colors.green1};
  color: #fff;
  border: none;
  border-radius: 8px;
  height: 40px;
  padding: 0 16px;
  font-size: 16px;
  font-weight: 400;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #047857;
  }
`;

const MainContent = styled.div`
  flex: 1;
  padding: 52px 72px;
  gap: 32px;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const CardWrapper = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${theme.colors.grey2};
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SearchWrapper = styled.div`
  width: 240px;
  display: flex;
  background-color: ${theme.colors.grey4};
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  padding: 0px 8px;
  align-items: center;
  box-sizing: border-box;
  gap: 8px;
`;

const CustomSearch = styled(Input)`
  border-radius: 12px;
  height: 40px;
  font-size: 16px;
  border: none;
  box-shadow: none;
  background-color: ${theme.colors.grey4};
  padding: 0;

  &:hover,
  &:focus {
    border: none;
    box-shadow: none;
    background-color: ${theme.colors.grey4};
  }
`;

const EmptyStateTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    background-color: white;
    border-bottom: none;
    padding: 10px 16px;
    font-size: 14px;
    line-height: 20px;
    color: ${theme.colors.grey1};
    font-weight: 500;
  }

  .ant-table-thead > tr > th::before {
    display: none;
  }

  .ant-table-tbody > tr > td {
    padding: 16px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    border-bottom: none;
  }

  .ant-table-tbody > tr:nth-child(odd) {
    background-color: ${theme.colors.grey3};
  }

  .ant-table-tbody > tr:nth-child(even) {
    background-color: ${theme.colors.grey4};
  }

  .ant-table table {
    border-spacing: 0 4px;
    border-collapse: separate;
  }

  .ant-table-tbody > tr > td:last-child {
    border-top-right-radius: 16px;
    border-bottom-right-radius: 16px;
  }

  /* Hide default AntD sort icon */
  .ant-table-column-sorters .ant-table-column-sorter {
    display: none !important;
  }
`;
